import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';

interface Msg {
  id: string; sender_id: string; body: string; created_at: string;
  attachment_path: string | null; attachment_type: string | null;
  attachment_name: string | null; attachment_mime: string | null;
}

const MAX_BYTES = 25 * 1024 * 1024;

/**
 * 1:1 chat, used for both conversation kinds: pass `applicationId` for a campaign
 * thread (business ↔ applicant) or `brandThreadId` for a direct creator ↔ brand
 * thread. They live in separate tables, but attachments, read receipts and polling
 * are identical, so this only swaps the table and key column. RLS
 * (is_campaign_thread_participant / is_brand_thread_participant) gates both.
 * Attachments go to the private chat-uploads bucket under the thread id, served by
 * signed URLs. Polls every 5s — no realtime dependency. Full-screen on mobile.
 */
export function CampaignChat({ applicationId, brandThreadId, title, subtitle, onClose }:
  { applicationId?: string; brandThreadId?: string; title: string; subtitle?: string; onClose: () => void }) {
  const isBrand = !!brandThreadId;
  const threadKey = (brandThreadId ?? applicationId)!;      // storage folder + query key
  const msgTable = isBrand ? 'brand_messages' : 'campaign_messages';
  const msgIdCol = isBrand ? 'thread_id' : 'application_id';
  const readsTable = isBrand ? 'brand_thread_reads' : 'campaign_thread_reads';
  const readsConflict = isBrand ? 'thread_id,user_id' : 'application_id,user_id';
  const { t } = useTranslation('common');
  const tr = (k: string, d: string) => t(k, { defaultValue: d }) as string;
  const { user } = useAuth();
  const invalidate = useInvalidateSupabase();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  // Make the iOS "swipe-right / back" gesture close the chat (and the image
  // viewer) instead of navigating the underlying route. We push one history
  // entry on open; a back gesture pops it -> popstate -> we close. Explicit
  // closes (×, backdrop) go through history.back() so both paths behave the same.
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const lightboxRef = useRef(lightbox); lightboxRef.current = lightbox;
  useEffect(() => {
    window.history.pushState({ chatOpen: true }, '');
    const onPop = () => {
      if (lightboxRef.current) { setLightbox(null); window.history.pushState({ chatOpen: true }, ''); }
      else onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const close = () => window.history.back();
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: msgs } = useSupabaseQuery<Msg[]>(
    ['chat-messages', msgTable, threadKey],
    async () => supabase.from(msgTable)
      .select('id, sender_id, body, created_at, attachment_path, attachment_type, attachment_name, attachment_mime')
      .eq(msgIdCol, threadKey).order('created_at', { ascending: true }),
    { enabled: !!threadKey, refetchInterval: 5000, refetchOnWindowFocus: true },
  );
  const messages = useMemo(() => msgs ?? [], [msgs]);

  // Sign every attachment path in one batch; refresh well before the 1h expiry.
  const paths = useMemo(
    () => messages.map((m) => m.attachment_path).filter((p): p is string => !!p),
    [messages],
  );
  const { data: signed } = useSupabaseQuery<{ path: string | null; signedUrl: string }[]>(
    ['chat-signed', threadKey, paths.join(',')],
    async () => paths.length
      ? supabase.storage.from('chat-uploads').createSignedUrls(paths, 3600)
      : { data: [], error: null },
    { enabled: paths.length > 0, staleTime: 45 * 60_000, refetchInterval: 45 * 60_000 },
  );
  const urlByPath = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of signed ?? []) if (s.path && s.signedUrl) m.set(s.path, s.signedUrl);
    return m;
  }, [signed]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  // Opening the thread (and any message arriving while it's open) marks it read.
  useEffect(() => {
    if (!threadKey) return;
    void supabase.from(readsTable)
      .upsert({ [msgIdCol === 'thread_id' ? 'thread_id' : 'application_id']: threadKey, last_read_at: new Date().toISOString() }, { onConflict: readsConflict })
      .then(() => invalidate(['unread-threads']));
  }, [threadKey, readsTable, readsConflict, msgIdCol, messages.length, invalidate]);

  const send = async () => {
    const b = text.trim();
    if (!b || sending) return;
    setSending(true);
    const { error } = await supabase.from(msgTable).insert({ [msgIdCol]: threadKey, body: b });
    setSending(false);
    if (error) { setErr(error.message); return; }
    setText(''); setErr(null);
    invalidate(['chat-messages', msgTable, threadKey]);
  };

  const onPick = async (list: FileList | null) => {
    const f = list?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!f || uploading) return;
    if (f.size > MAX_BYTES) { setErr(tr('chat.tooBig', 'Файл слишком большой (до 25 МБ).')); return; }
    setErr(null); setUploading(true);
    try {
      const ext = (f.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) || 'bin';
      const path = `${threadKey}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('chat-uploads').upload(path, f, { upsert: false, contentType: f.type || undefined });
      if (upErr) throw upErr;
      const type = f.type.startsWith('image/') ? 'image' : f.type.startsWith('video/') ? 'video' : 'file';
      const { error: insErr } = await supabase.from(msgTable).insert({
        [msgIdCol]: threadKey, body: text.trim(),
        attachment_path: path, attachment_type: type, attachment_name: f.name, attachment_mime: f.type || null,
      });
      if (insErr) throw insErr;
      setText('');
      invalidate(['chat-messages', msgTable, threadKey]);
    } catch (e) {
      setErr((e as Error).message || tr('chat.uploadFail', 'Не удалось загрузить файл.'));
    } finally {
      setUploading(false);
    }
  };

  const fmtTime = (iso: string) => {
    try { return new Date(iso).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }); }
    catch { return ''; }
  };

  const renderAttachment = (m: Msg, mine: boolean) => {
    if (!m.attachment_path) return null;
    const url = urlByPath.get(m.attachment_path);
    if (!url) return <div style={{ color: mine ? '#06101f' : 'var(--dim)', fontSize: 12, opacity: 0.7 }}>{tr('chat.loadingFile', 'загрузка…')}</div>;
    if (m.attachment_type === 'image') {
      return (
        <img src={url} alt={m.attachment_name ?? ''} onClick={() => setLightbox(url)}
          style={{ maxWidth: 220, maxHeight: 260, borderRadius: 8, display: 'block', objectFit: 'cover', cursor: 'pointer' }} />
      );
    }
    if (m.attachment_type === 'video') {
      return <video src={url} controls playsInline style={{ maxWidth: 240, maxHeight: 280, borderRadius: 8, display: 'block' }} />;
    }
    return (
      <a href={url} target="_blank" rel="noreferrer" download={m.attachment_name ?? undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: mine ? '#06101f' : 'var(--accent-text)', textDecoration: 'none', fontSize: 13, padding: '4px 2px', wordBreak: 'break-word' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
        <span>{m.attachment_name ?? tr('chat.file', 'Файл')}</span>
      </a>
    );
  };

  // Portal to <body> so the chat always sits above everything — including the
  // support widget (z-index 60) and, on the business side, the CampaignApplicants
  // modal (z-index 50) it's opened from (which would otherwise confine it).
  return createPortal(
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, background: '#0e1119', display: 'flex', flexDirection: 'column', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', paddingTop: 'calc(12px + env(safe-area-inset-top))', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
            {subtitle && <div style={{ color: 'var(--dim)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>}
          </div>
          <button onClick={close} aria-label={tr('chat.close', 'Закрыть')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--text)', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, lineHeight: 1, fontFamily: 'inherit', flexShrink: 0 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.length === 0 && (
            <div style={{ color: 'var(--dim)', textAlign: 'center', margin: 'auto', fontSize: 13, padding: 20 }}>{tr('chat.empty', 'Сообщений пока нет. Напишите первым.')}</div>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user?.id;
            const hasAttachment = !!m.attachment_path;
            return (
              <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <div style={{ background: mine ? 'rgba(74,158,255,0.92)' : 'rgba(255,255,255,0.07)', color: mine ? '#06101f' : 'var(--text)', padding: hasAttachment ? 6 : '8px 12px', borderRadius: 12, borderBottomRightRadius: mine ? 4 : 12, borderBottomLeftRadius: mine ? 12 : 4, fontSize: 14, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {renderAttachment(m, mine)}
                  {m.body && <span style={{ padding: hasAttachment ? '0 6px 2px' : 0 }}>{m.body}</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>{fmtTime(m.created_at)}</div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        {err && <div style={{ color: '#e8607a', fontSize: 12, padding: '6px 14px 0' }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '10px 12px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" onChange={(e) => onPick(e.target.files)} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} aria-label={tr('chat.attach', 'Прикрепить файл')} title={tr('chat.attach', 'Прикрепить файл')}
            style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: uploading ? 'var(--accent)' : 'var(--muted)', cursor: uploading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {uploading ? (
              <span style={{ fontSize: 11 }}>…</span>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
            )}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            rows={1}
            placeholder={tr('chat.placeholder', 'Сообщение…')}
            style={{ flex: 1, resize: 'none', minHeight: 40, maxHeight: 120, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', color: 'var(--text)', border: '1px solid rgba(255,255,255,0.12)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }}
          />
          <button onClick={send} disabled={sending || !text.trim()} style={{ background: 'var(--accent)', color: '#06101f', border: 'none', borderRadius: 10, padding: '0 16px', height: 40, fontWeight: 600, cursor: sending || !text.trim() ? 'default' : 'pointer', fontFamily: 'inherit', opacity: sending || !text.trim() ? 0.5 : 1, flexShrink: 0 }}>{tr('chat.send', 'Отпр.')}</button>
        </div>
      </div>

      {/* In-app image viewer — tap anywhere to close. Stays inside the app; never
          navigates to the raw signed storage URL. */}
      {lightbox && (
        <div onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} aria-label={tr('chat.close', 'Закрыть')}
            style={{ position: 'absolute', top: 'calc(14px + env(safe-area-inset-top))', right: 14, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', width: 40, height: 40, borderRadius: 20, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
      )}
    </div>,
    document.body,
  );
}
